import { styled } from '@mui/material/styles';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import DeleteIcon from '@mui/icons-material/DeleteRounded';
import EditIcon from '@mui/icons-material/EditRounded';
import ActionIconButton from '@components/ActionIconButton';
import { Checkbox } from '@components/Checkbox';
import { useTranslation } from '@hooks';
import TableCellCheckbox from './TableCellCheckbox';
import { renderCellContent } from './renderCellContent';
import { TABLE_BODY_CELL_HEIGHT, TABLE_BODY_CELL_PADDING } from './tableLayout';
import { guardedRowClick } from './rowClick';

const TableCellStyled = styled(TableCell)(() => ({
    padding: TABLE_BODY_CELL_PADDING,
    minHeight: TABLE_BODY_CELL_HEIGHT,
    height: TABLE_BODY_CELL_HEIGHT,
    '& .MuiIconButton-root': {
        padding: 8,
        '& svg': {
            color: 'var(--text-secondary)',
            width: 20,
            height: 20,
        },
    },
}));

const ActionsWrapper = styled('div')(() => ({
    display: 'flex',
    gap: 16,
}));

const TableRowStyled = styled(TableRow)(() => ({
    '&.MuiTableRow-hover:hover': {
        background: 'var(--action-highlight)',
    },
}));

const ActionComponent = ({ cellItem, isCustomActionComponent, onPrimaryAction, onSecondaryAction, t }) => {
    if (isCustomActionComponent) {
        return renderCellContent(cellItem);
    }
    return (
        <ActionsWrapper>
            {onPrimaryAction && (
                <ActionIconButton title={t('common.edit')} onClick={() => onPrimaryAction(cellItem)}>
                    <EditIcon fontSize="inherit" />
                </ActionIconButton>
            )}
            {onSecondaryAction && (
                <ActionIconButton title={t('common.delete')} onClick={() => onSecondaryAction(cellItem)}>
                    <DeleteIcon fontSize="inherit" />
                </ActionIconButton>
            )}
        </ActionsWrapper>
    );
};

const TableRowWithActions = ({
    rowData,
    onPrimaryAction,
    onSecondaryAction,
    isCustomActionComponent,
    withCheckbox,
    checked,
    onSelect,
    onRowClick,
}) => {
    const { t } = useTranslation();

    const actionCell = rowData?.[rowData.length - 1];
    const hasBuiltInPrimaryAction = Boolean(onPrimaryAction && !isCustomActionComponent && actionCell?.value);
    const rowAction = onRowClick || (hasBuiltInPrimaryAction ? () => onPrimaryAction(actionCell) : undefined);

    return (
        <TableRowStyled hover sx={rowAction ? { cursor: 'pointer' } : undefined} onClick={guardedRowClick(rowAction)}>
            {withCheckbox && (
                <TableCellCheckbox>
                    <Checkbox checked={checked} onChange={onSelect} />
                </TableCellCheckbox>
            )}
            {rowData?.length &&
                rowData.map((cell, position) =>
                    position + 1 < rowData.length ? (
                        <TableCellStyled
                            align={position === 0 ? 'left' : 'center'}
                            component="th"
                            scope="row"
                            key={`row-cell-${cell?.value}-${position}`}
                        >
                            {renderCellContent(cell)}
                        </TableCellStyled>
                    ) : (
                        <TableCellStyled
                            sx={{ padding: 0, width: 104 }}
                            align="right"
                            key={`row-cell-${cell?.value}-${position}`}
                        >
                            {cell.value && (
                                <ActionComponent
                                    cellItem={cell}
                                    onPrimaryAction={onPrimaryAction}
                                    onSecondaryAction={onSecondaryAction}
                                    isCustomActionComponent={isCustomActionComponent}
                                    t={t}
                                />
                            )}
                        </TableCellStyled>
                    ),
                )}
        </TableRowStyled>
    );
};

export default TableRowWithActions;
